<script>
export default {
    name: 'BaseCalendarViewWeek',
};
</script>

<script setup>
import { useNow } from '@vueuse/core';
import { computed, ref, watchEffect } from 'vue';
import { addUnit } from '@/components/base-components/utils/dom';
import { getWeekdays, getWeeklyCalendarGrid } from '@/components/base-components/components/calendar/utils/ranges.js';

const props = defineProps({
    date:           {
        type:     Date,
        required: true,
    },
    firstDayOfWeek: {
        type:    Number,
        default: 1,
    },
    noWeekends:     {
        type: Boolean,
    },
    titleClass:     {
        type: [String, Array, Object],
    },
    gridClass:      {
        type: [String, Array, Object],
    },
});

const emit = defineEmits({
    range: null,
});

const days = computed(() => getWeekdays(props.date, props.firstDayOfWeek, props.noWeekends));
const cells = computed(() => getWeeklyCalendarGrid(props.date, props.firstDayOfWeek, props.noWeekends));

watchEffect(() => {
    emit('range', days.value.map(({ date }) => date));
});

const gridRef = ref(null);
const now = useNow({ interval: 10000 });

const pointerCellStyle = computed(() => {
    const leftOffset = days.value.find(day => day.isToday)?.index;

    if (!leftOffset) return null;

    return {
        gridColumnStart: leftOffset + 1,
        gridRowStart:    now.value.getHours() + 2,
    };
});

const pointerStyle = computed(() => ({
    top:       addUnit(Math.round(now.value.getMinutes() * 100 / 60), '%'),
    translate: '0 -50%',
}));
</script>

<template>
    <div
        ref="gridRef"
        class="grid"
        :class="[
            gridClass || [
                'grid-rows-[auto_repeat(24,1fr)]',
                noWeekends ? 'grid-cols-[auto_repeat(5,1fr)]' : 'grid-cols-[auto_repeat(7,1fr)]',
            ],
        ]"
    >
        <template v-for="({ date, ...cell }, index) in days">
            <slot
                v-if="index === 0"
                v-bind="cell"
                name="title"
                :style="{
                    gridColumnStart: 1,
                    gridRowStart: 1,
                }"
            />

            <slot
                v-bind="{ date, ...cell }"
                name="title"
                :index="index"
                :style="{
                    gridColumnStart: index + 2,
                    gridRowStart: 1,
                }"
            />
        </template>

        <template v-for="({ date, ...cell }, index) in cells">
            <slot
                v-if="cell.xIndex === 0"
                v-bind="cell"
                name="cell"
                :styles="{
                    gridColumnStart: 1,
                    gridRowStart: cell.yIndex + 2,
                }"
            />

            <slot
                v-bind="{ date, ...cell }"
                name="cell"
                :index="index"
                :styles="{
                    gridColumnStart: cell.xIndex + 2,
                    gridRowStart: cell.yIndex + 2,
                }"
            />
        </template>

        <slot name="content" :days="days" />

        <slot
            v-if="pointerCellStyle"
            name="pointer-cell"
            :cell-style="pointerCellStyle"
            :pointer-style="pointerStyle"
        />
    </div>
</template>
